import DsConversation from 'components/Panel/Chat/Conversation/DsConversation';
import { focusChatInput } from 'components/Panel/Chat/hooks/chatInputFocus';
import { Loader } from 'components/Widget/Loader';
import { MenuItemAction } from 'menu/MenuItem';
import { useCallback, useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveDesignSystem, selectDsChatRefs } from 'redux/db/reducers';
import { designSystemEditingBlocked } from 'redux/db/util';
import { closeDsChatTab, closeOtherDsChatTabs, setActiveDsChatId } from 'redux/mode/actions';
import { displayDsReadOnlyModal } from 'redux/mode/util/modals';
import { replaceDsChatWithDraft, startDsChatDraft } from 'redux/mode/util';
import { RootReducer, selectActiveDsChatOrDraft, selectDsChatTabs } from 'redux/reducers';
import { Header } from '../Header';
import * as Base from '../styles';

const DsChatPanel = () => {
	const dispatch = useDispatch();

	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

	const {
		dsChatRefs,
		tabs,
		activeDsChatId,
		activeDesignSystem,
		activeChat,
		activeChatIsEmpty,
		draftChatId,
		chatRefsLoaded,
		dsVersionLoaded,
		chatsLoading,
	} = useSelector((r: RootReducer) => {
		const activeDsChatId = r.mode.activeDsChatId;
		const dsChatRefs = selectDsChatRefs(r.db);
		const activeChat = selectActiveDsChatOrDraft(r);

		return {
			dsChatRefs,
			tabs: selectDsChatTabs(r),
			activeDsChatId,
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeChat,
			// An empty chat is already a new chat, so "+" has nothing to add.
			activeChatIsEmpty: !!activeChat && activeChat.entries.length === 0,
			// A brand-new chat is a client-only draft, so it is intentionally absent from
			// dsChatRefs and has no server row to rename or copy an id from.
			draftChatId: r.mode.draftDsChat?.id ?? null,
			chatRefsLoaded: r.db.loading.dsChatRefs === 'loaded',
			dsVersionLoaded: r.db.loading.activeDesignSystemVersion === 'loaded',
			// The loader means "there is nothing to show", not "a request is in
			// flight". Whenever we hold a chat — a draft, an optimistic insert, or a
			// polled one — render it, so a chat that is mid-sync never blinks out.
			chatsLoading: activeDsChatId !== null && activeChat === null,
		};
	}, shallowEqual);

	// Chat refs are already sorted by updatedAt DESC from the backend
	const sortedChatRefs = dsChatRefs;

	// Falls back to the newest chat when the active one resolves to nothing. Holding a
	// chat is the signal to leave it alone: a draft or a freshly created chat is
	// legitimately missing from dsChatRefs, and reselecting would throw it away.
	// Only select once the list has loaded — an unloaded list can't say which chat
	// is newest.
	useEffect(() => {
		if (activeChat) return;
		if (!chatRefsLoaded) return;

		if (!activeDsChatId || !sortedChatRefs.some((ref) => ref.id === activeDsChatId)) {
			const firstChatRef = sortedChatRefs.length > 0 ? sortedChatRefs[0] : null;
			if (firstChatRef) {
				dispatch(setActiveDsChatId(firstChatRef.id));
			}
		}
	}, [sortedChatRefs, chatRefsLoaded, activeDsChatId, dispatch, activeChat]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const isEditable = (): boolean => {
		if (!activeDesignSystem) return false;
		const blocked = designSystemEditingBlocked();
		if (blocked && blocked !== 'historical') {
			displayDsReadOnlyModal(blocked);
			return false;
		}
		return true;
	};

	// One click always lands the user typing in an empty chat. An empty chat that is
	// already open IS that chat, so it is focused rather than duplicated — which caps
	// stray clicks at the one empty tab.
	const onNewChat = () => {
		if (!isEditable()) return;
		if (draftChatId) {
			dispatch(setActiveDsChatId(draftChatId));
		} else if (!activeChatIsEmpty || !activeDsChatId) {
			// The new chat is a client-only draft; it hits the server only once its first
			// message is sent.
			startDsChatDraft();
		}
		focusChatInput();
	};

	const onReplaceTabWithNewChat = (targetId: string) => {
		if (!isEditable()) return;
		replaceDsChatWithDraft(targetId);
		focusChatInput();
	};

	const buildChatMenuItems = useCallback(
		(chatId: string) => {
			if (chatId === draftChatId) return [];
			return [MenuItemAction.createRenameDsChat(chatId), MenuItemAction.createCopyChatId(chatId)];
		},
		[draftChatId]
	);

	return (
		<Base.Base ref={ref}>
			<Header
				tabs={tabs}
				activeChatId={activeDsChatId}
				chats={sortedChatRefs}
				onSelectChat={(id: string) => dispatch(setActiveDsChatId(id))}
				onNewChat={onNewChat}
				onCloseTab={(id: string) => dispatch(closeDsChatTab(id))}
				onCloseOtherTabs={(id: string) => dispatch(closeOtherDsChatTabs(id))}
				onReplaceTabWithNewChat={onReplaceTabWithNewChat}
				buildChatMenuItems={buildChatMenuItems}
			/>
			{activeDsChatId ? (
				<Base.Conversation>
					{chatsLoading ? (
						<Base.LoadingContainer>
							<Loader size={32} />
						</Base.LoadingContainer>
					) : (
						<DsConversation />
					)}
				</Base.Conversation>
			) : activeDesignSystem && !dsVersionLoaded ? (
				<Base.LoadingContainer>
					<Loader size={24} />
				</Base.LoadingContainer>
			) : null}
		</Base.Base>
	);
};

export default DsChatPanel;
